import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../../../components/Common/Actions";
import FilledButton from "../../../../components/Common/Buttons/FilledButton";
import CommonFilterModal from "../../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../../components/Common/TabBar";
import ListPageLayout from "../../../../components/Layout";
import Deletepopup from "../../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../../components/Utils/hooks/useSortHandler";
import {
  PartneredPharmaciesBreadcrumbs,
  PartneredPharmaciesnavBarData,
  statuses,
} from "../../Utils/MasterConst";

const PrimarySpecialisation = () => {
  const navigate = useNavigate();

  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    "Pharmacy Business Type": [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    "Pharmacy Business Type": [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState(
    "Pharmacy Business Type",
  );
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const [loader, setLoader] = useState(true);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const addNewBusinessType = {
    id: "addnewpost",
    onClick: () => navigate("/master-partnered-pharmacy-create"),
    style: { width: "240px" },
    label: "Add New Business Type",
  };
  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}pharmacy-business-type/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "pharmacy-business-type",
    fetchData,
    setShowDelete,
  );

  const fetchFilteringArray = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}pharmacy-business-type/getarray`,
      );
      setFilteringKey(res.data);
    } catch (err) {
      console.error(err);
      alert("Error loading filter options");
    }
  }, []);

  useEffect(() => {
    fetchFilteringArray();
  }, [fetchFilteringArray]);

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Pharmacy Business Type",
      dataIndex: "type",
      width: 280,
      key: "type",
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 150,
      render: (active) => (active ? "Verified" : "Not Verified"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <Actions
            isEdit={true}
            isView={true}
            isDelete={true}
            onClickView={() =>
              navigate(`/master-partnered-pharmacy-view/${record._id}`)
            }
            onClickDelete={() => {
              setDeleteId(record._id);
              setShowDelete(true);
            }}
            onClickEdit={() =>
              navigate(`/master-partnered-pharmacy-edit/${record._id}`)
            }
          />
        </div>
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    "Pharmacy Business Type": filteringKey,
    Status: statuses,
  };

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <PartneredPharmaciesBreadcrumbs last="Pharmacy Business Type" />
      }
      addButton={<FilledButton props={addNewBusinessType} />}
      titles={<NavBarTitles titles={PartneredPharmaciesnavBarData} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ "Pharmacy Business Type": [], Status: [] });
          setSelectedValues({ "Pharmacy Business Type": [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default PrimarySpecialisation;
